import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Loader2, CheckCircle2, FileText, Gauge, Search, CreditCard, ShieldCheck, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { EASE, Reveal, PageHero, SectionHead } from "@/components/shared";
import { API, formatApiError } from "@/lib/api";
import { IMAGES } from "@/lib/content";

const BUSINESS_TYPES = ["Hotel / Resort", "Villa / Guesthouse", "Tour Operator", "Restaurant / Bar", "Professional Services", "Financial Services", "Retail / Other"];
const FRICTIONS = ["Not enough direct bookings", "Website is slow or dated", "Too much manual admin", "Can't take online payments", "Hard to be found on Google", "Something else"];
const HOURS = [9, 10, 11, 12, 13, 14, 15, 16];

const EMPTY = { business_type: "", website: "", friction: "", details: "", slot: "", slot_label: "", name: "", business: "", email: "", phone: "" };

const pad = (n) => String(n).padStart(2, "0");
const hourLabel = (h) => (h < 12 ? `${h}:00 AM` : h === 12 ? "12:00 PM" : `${h - 12}:00 PM`);

export default function Audit() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [day, setDay] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const days = useMemo(() => {
    const out = [];
    const d = new Date();
    while (out.length < 10) {
      d.setDate(d.getDate() + 1);
      const dow = d.getDay();
      if (dow >= 1 && dow <= 5) out.push(new Date(d));
    }
    return out;
  }, []);

  const dayLabel = (d) => d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });

  const pickSlot = (h) => {
    if (!day) return;
    const iso = `${day}T${pad(h)}:00:00-04:00`;
    set("slot", iso);
    set("slot_label", `${dayLabel(new Date(`${day}T12:00:00`))}, ${hourLabel(h)} AST`);
  };

  const canNext =
    (step === 0 && form.business_type) ||
    (step === 1 && form.friction) ||
    step >= 2;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await API.post("/submissions", {
        type: "audit",
        name: form.name,
        email: form.email,
        phone: form.phone,
        business: form.business,
        website: form.website,
        message: form.details,
        meta: {
          business_type: form.business_type,
          friction: form.friction,
          ...(form.slot ? { scheduled_slot: form.slot, call_time: form.slot_label } : {}),
        },
      });
      setDone(true);
      if (window.gtag) window.gtag("event", "generate_lead", { form: "digital_audit", scheduled: !!form.slot });
      toast.success("Audit request received. Your bespoke report is being scheduled.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const receives = [
    { icon: FileText, title: "15-page bespoke analysis", text: "Your digital presence reviewed by a senior consultant — not an automated export." },
    { icon: Gauge, title: "Speed & mobile audit", text: "Real-device testing on the connections your guests actually use." },
    { icon: Search, title: "Caribbean search visibility", text: "Where you rank when visitors search from London, New York and Toronto." },
    { icon: CreditCard, title: "Payment readiness check", text: "Exactly what stands between you and friction-free international payments." },
  ];

  const STEP_LABELS = ["Your business", "The friction", "Call time", "Contact"];

  return (
    <main data-testid="audit-page">
      <PageHero
        eyebrow="Free Digital Audit"
        title="Discover your unclaimed direct revenue."
        lead="In one week you receive a plain-English, fifteen-page analysis of your digital presence — and a prioritised list of what to fix first. Free for Antigua & Barbuda businesses."
        image={IMAGES.harbor}
      />

      <section className="pb-24 lg:pb-32">
        <div className="mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="gold-ring-card p-8 lg:p-10" data-testid="audit-form-card">
                {done ? (
                  <div className="flex flex-col items-center gap-5 py-14 text-center" data-testid="audit-success">
                    <CheckCircle2 className="h-16 w-16 text-gold" />
                    <h2 className="font-serif text-3xl text-ivory">Your audit is booked in.</h2>
                    <p className="max-w-md text-sm font-light leading-relaxed text-slate-400">
                      {form.slot_label
                        ? `Your call is requested for ${form.slot_label}. We will confirm by email within one business day.`
                        : "A senior consultant will review your digital presence and deliver your report within seven days."}
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="mb-8 flex items-center gap-3" data-testid="audit-progress">
                      {STEP_LABELS.map((label, i) => (
                        <div key={label} className="flex flex-1 items-center gap-3">
                          <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border font-mono text-xs ${i <= step ? "border-gold bg-gold/15 text-gold-glow" : "border-slate-600 text-slate-500"}`}>
                            {i + 1}
                          </div>
                          <span className={`hidden text-xs lg:block ${i <= step ? "text-ivory" : "text-slate-500"}`}>{label}</span>
                          {i < STEP_LABELS.length - 1 && <div className={`h-px flex-1 ${i < step ? "bg-gold" : "bg-slate-700"}`} />}
                        </div>
                      ))}
                    </div>

                    <form onSubmit={submit} data-testid="audit-form">
                      <AnimatePresence mode="wait">
                        {step === 0 && (
                          <motion.div key="s0" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.4, ease: EASE }}>
                            <p className="font-serif text-xl text-ivory">What kind of business do you run?</p>
                            <div className="mt-5 flex flex-wrap gap-2.5" data-testid="audit-business-types">
                              {BUSINESS_TYPES.map((t) => (
                                <button
                                  type="button"
                                  key={t}
                                  onClick={() => set("business_type", t)}
                                  data-testid={`audit-type-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                  className={`rounded-full border px-4 py-2 text-xs font-medium transition-colors duration-300 ${form.business_type === t ? "border-gold bg-gold/15 text-gold-glow" : "border-slate-600 text-slate-300 hover:border-gold/50"}`}
                                >
                                  {t}
                                </button>
                              ))}
                            </div>
                            <input value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="Current website address (if you have one)" className="input-lux mt-6" data-testid="audit-website-input" />
                          </motion.div>
                        )}

                        {step === 1 && (
                          <motion.div key="s1" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.4, ease: EASE }}>
                            <p className="font-serif text-xl text-ivory">What frustrates you most right now?</p>
                            <div className="mt-5 grid gap-2.5 sm:grid-cols-2" data-testid="audit-frictions">
                              {FRICTIONS.map((f) => (
                                <button
                                  type="button"
                                  key={f}
                                  onClick={() => set("friction", f)}
                                  data-testid={`audit-friction-${f.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                  className={`rounded-xl border px-4 py-3 text-left text-xs font-medium transition-colors duration-300 ${form.friction === f ? "border-gold bg-gold/15 text-gold-glow" : "border-slate-600 text-slate-300 hover:border-gold/50"}`}
                                >
                                  {f}
                                </button>
                              ))}
                            </div>
                            <textarea value={form.details} onChange={(e) => set("details", e.target.value)} rows={3} placeholder="Anything else we should know? (optional)" className="input-lux mt-5 resize-none" data-testid="audit-details-input" />
                          </motion.div>
                        )}

                        {step === 2 && (
                          <motion.div key="s2" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.4, ease: EASE }}>
                            <div className="flex items-center gap-3">
                              <CalendarClock className="h-5 w-5 text-gold" />
                              <p className="font-serif text-xl text-ivory">Pick a call time — optional</p>
                            </div>
                            <p className="mt-2 text-xs font-light text-slate-400">
                              Thirty minutes with the founder to walk through your report. All times Antigua (AST).
                            </p>
                            <select
                              value={day}
                              onChange={(e) => { setDay(e.target.value); set("slot", ""); set("slot_label", ""); }}
                              className="input-lux mt-5"
                              data-testid="audit-day-select"
                            >
                              <option value="" className="bg-navy-deep">Choose a day…</option>
                              {days.map((d) => (
                                <option key={d.toISOString()} value={`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`} className="bg-navy-deep">
                                  {dayLabel(d)}
                                </option>
                              ))}
                            </select>
                            {day && (
                              <div className="mt-4 grid grid-cols-4 gap-2" data-testid="audit-time-slots">
                                {HOURS.map((h) => (
                                  <button
                                    type="button"
                                    key={h}
                                    onClick={() => pickSlot(h)}
                                    data-testid={`audit-slot-${h}`}
                                    className={`rounded-xl border px-2 py-2.5 text-xs font-medium transition-colors duration-300 ${form.slot.endsWith(`T${pad(h)}:00:00-04:00`) ? "border-gold bg-gold/15 text-gold-glow" : "border-slate-600 text-slate-300 hover:border-gold/50"}`}
                                  >
                                    {hourLabel(h)}
                                  </button>
                                ))}
                              </div>
                            )}
                            {form.slot_label && (
                              <p className="mt-4 rounded-xl border border-gold/30 bg-gold/10 px-4 py-3 text-xs text-gold-glow" data-testid="audit-slot-confirm">
                                Call requested: {form.slot_label}
                              </p>
                            )}
                            <button type="button" onClick={() => setStep(3)} className="mt-3 text-xs text-slate-500 underline underline-offset-2 hover:text-slate-300" data-testid="audit-skip-slot">
                              Skip — decide the time later
                            </button>
                          </motion.div>
                        )}

                        {step === 3 && (
                          <motion.div key="s3" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.4, ease: EASE }}>
                            <p className="font-serif text-xl text-ivory">Where do we send the report?</p>
                            <div className="mt-5 grid gap-4 sm:grid-cols-2">
                              <input required value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Full name" className="input-lux" data-testid="audit-name-input" />
                              <input required value={form.business} onChange={(e) => set("business", e.target.value)} placeholder="Business name" className="input-lux" data-testid="audit-business-input" />
                              <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="Email address" className="input-lux" data-testid="audit-email-input" />
                              <input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Phone / WhatsApp (optional)" className="input-lux" data-testid="audit-phone-input" />
                            </div>
                            {form.slot_label && (
                              <p className="mt-4 text-xs text-slate-400">Call requested: <span className="text-gold-glow">{form.slot_label}</span></p>
                            )}
                            <p className="mt-4 flex items-center gap-2 text-[11px] text-slate-500">
                              Your answers are used only to prepare your report and are not shared.
                            </p>
                          </motion.div>
                        )}
                      </AnimatePresence>

                      <div className="mt-8 flex items-center justify-between">
                        <button
                          type="button"
                          onClick={() => setStep(step - 1)}
                          disabled={step === 0}
                          data-testid="audit-back-btn"
                          className="flex items-center gap-2 text-sm text-slate-400 transition-colors duration-300 hover:text-ivory disabled:opacity-0"
                        >
                          <ArrowLeft className="h-4 w-4" /> Back
                        </button>
                        {step < 3 ? (
                          <button
                            type="button"
                            onClick={() => canNext && setStep(step + 1)}
                            disabled={!canNext}
                            data-testid="audit-next-btn"
                            className="group flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-semibold text-navy-deep transition-colors duration-300 hover:bg-gold-light disabled:opacity-40"
                          >
                            Continue <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                          </button>
                        ) : (
                          <button
                            type="submit"
                            disabled={loading || !form.name || !form.email || !form.business}
                            data-testid="audit-form-submit-btn"
                            className="group flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-semibold text-navy-deep transition-colors duration-300 hover:bg-gold-light disabled:opacity-40"
                          >
                            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Request my free audit <ArrowRight className="h-4 w-4" /></>}
                          </button>
                        )}
                      </div>
                    </form>
                  </>
                )}
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-5">
            <SectionHead eyebrow="What you receive" title="Not a PDF generator. A consultant's eye." />
            <div className="mt-10 space-y-5">
              {receives.map((r, i) => (
                <Reveal key={r.title} delay={i * 0.1}>
                  <div className="flex gap-5 rounded-2xl border border-gold/15 bg-navy-deep/50 p-6" data-testid={`audit-receive-${i}`}>
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-gold/30 bg-gold/10">
                      <r.icon className="h-5 w-5 text-gold-glow" />
                    </div>
                    <div>
                      <h3 className="font-serif text-lg text-ivory">{r.title}</h3>
                      <p className="mt-1.5 text-sm font-light text-slate-400">{r.text}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
              <Reveal delay={0.4}>
                <div className="flex items-center gap-3 rounded-2xl border border-gold/25 bg-gold/5 p-5">
                  <ShieldCheck className="h-5 w-5 shrink-0 text-gold" />
                  <p className="text-xs leading-relaxed text-slate-300">No obligation, no spam, no lock-in. Your details stay on the island with us.</p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
